import type { ManageSubscriptionSheetProduct } from "./types";

/**
 * WHICH PRODUCT are they actually cancelling?
 *
 * On a contract with 2+ lines the chat used to open with "what drew you to A and
 * B?" — unanswerable when the lines are unrelated (a drink and a face oil), and
 * it surfaced almost no usable reason. Worse, it framed the whole contract as the
 * thing being cancelled, when most people only want to drop ONE line. Cancelling
 * everything is the worst outcome available, and we were asking for it by default.
 *
 * So a 2+ line contract now opens on this question instead: one chip per line,
 * plus "Cancel everything". It is APP-RENDERED and deterministic — no LLM
 * round-trip, exactly like the greeting it precedes (cancel-chat-greeting.ts) —
 * and picking a line re-scopes the whole conversation to it (see
 * `buildCancelSubject(chosen, [])` in CancelSubscriptionChat).
 *
 * Pure, so the copy is testable without rendering the chat.
 */

/** The "cancel the whole contract" choice — not a product id. */
export const CANCEL_EVERYTHING = "__everything__" as const;

export type CancelEverything = typeof CANCEL_EVERYTHING;

export const WHICH_PRODUCT_QUESTION = "Which product would you like to cancel?";

export const CANCEL_EVERYTHING_LABEL = "Cancel everything";

export interface ProductChoiceOption {
  /** A line's product id, or the CANCEL_EVERYTHING sentinel. */
  value: number | CancelEverything;
  label: string;
}

/** A line of the contract, reduced to what this module needs. */
type Line = Pick<ManageSubscriptionSheetProduct, "id" | "title"> & {
  variant_title?: string | null;
};

function clean(s: string | null | undefined): string {
  return (s ?? "").trim();
}

/**
 * One chip per line (in contract order), then "Cancel everything" last — the
 * worst outcome never leads.
 *
 * Lines with no title are dropped: a nameless chip is unpickable. Two lines CAN
 * share a title (the same product on two variants), and two identical chips are
 * a coin toss — so when a title repeats, the variant disambiguates it.
 */
export function buildProductChoices(lines: readonly Line[]): ProductChoiceOption[] {
  const named = lines.filter((l) => clean(l.title).length > 0);
  const titleCounts = new Map<string, number>();
  for (const l of named) {
    const t = clean(l.title);
    titleCounts.set(t, (titleCounts.get(t) ?? 0) + 1);
  }
  const options: ProductChoiceOption[] = named.map((l) => {
    const title = clean(l.title);
    const variant = clean(l.variant_title);
    const ambiguous = (titleCounts.get(title) ?? 0) > 1;
    return {
      value: l.id,
      label: ambiguous && variant ? `${title} — ${variant}` : title,
    };
  });
  return [...options, { value: CANCEL_EVERYTHING, label: CANCEL_EVERYTHING_LABEL }];
}

/** "A", "A and B", "A, B and C" — the Oxford-free list used in customer copy. */
export function formatTitleList(titles: readonly string[]): string {
  const kept = [...new Set(titles.map(clean).filter(Boolean))];
  if (kept.length === 0) return "";
  if (kept.length === 1) return kept[0];
  return `${kept.slice(0, -1).join(", ")} and ${kept[kept.length - 1]}`;
}

/** Subject-verb agreement for a list rendered by formatTitleList. */
function keepsOrKeep(titles: readonly string[]): string {
  const kept = [...new Set(titles.map(clean).filter(Boolean))];
  return kept.length === 1 ? "keeps" : "keep";
}

/**
 * The confirm-remove ask. It MUST say the other lines survive: the customer is on
 * a cancel screen and has every reason to assume "confirm" ends everything. If we
 * don't say otherwise in the ask itself, a partial cancel reads as a full one and
 * they'll bail out of the very outcome we're trying to give them.
 *
 * Falls back to naming nothing (rather than "and  keeps shipping") when the
 * sibling titles are missing — the reassurance still lands.
 */
export function partialConfirmQuestion(args: {
  removedTitle: string;
  retainedTitles: readonly string[];
}): string {
  const removed = clean(args.removedTitle);
  const retained = formatTitleList(args.retainedTitles);
  const subject = removed ? `just ${removed}` : "just this product";
  return retained
    ? `Cancel ${subject}? ${retained} ${keepsOrKeep(args.retainedTitles)} shipping as normal.`
    : `Cancel ${subject}? The rest of your subscription keeps shipping as normal.`;
}

/** The terminal success card for a single-line removal (names what stays). */
export function partialSuccessCopy(args: {
  removedTitle: string;
  retainedTitles: readonly string[];
}): { title: string; message: string } {
  const removed = clean(args.removedTitle);
  const retained = formatTitleList(args.retainedTitles);
  return {
    title: removed ? `${removed} removed` : "Product removed",
    message: retained
      ? `We've removed ${removed || "it"} from your subscription. ${retained} ${keepsOrKeep(
          args.retainedTitles,
        )} shipping as normal — nothing else to do.`
      : `We've removed ${removed || "it"} from your subscription. The rest keeps shipping as normal.`,
  };
}
